import React from 'react';
import { motion } from 'framer-motion';

// Concepto: "La señal"
// Las frases aparecen en secuencia y la señal se dibuja como firma visual
// del concepto central.

const phraseVariants = {
  hidden: {
    opacity: 0,
    y: 12
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.9,
      ease: 'easeOut'
    }
  }
};
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 1.2
    }
  }
};
export function SignalSection() {
  return <section className="bg-gradient-section px-5 py-12 md:px-8 md:py-28" style={{
    paddingBottom: '0px'
  }} data-id="element-358" data-mp="3dp5qr5hneqpe">
      <div className="mx-auto max-w-2xl" data-id="element-359" data-mp="2zwptklbfhmwe">

        {/* Eyebrow */}
        <motion.p initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="mb-5 text-center font-sans text-[11px] font-semibold uppercase tracking-[0.28em] text-[#C8A870] md:mb-10" data-id="element-360" data-mp="34wlxylo1fgch">
          Lo que está pasando
        </motion.p>

        {/* Bloque narrativo */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.4
      }} className="text-left md:text-center" data-id="element-361" data-mp="1n2odfxjq8v83">

          {/* Frase 1 — protagonista */}
          <motion.h2 variants={phraseVariants} className="font-display font-bold leading-[1.02] tracking-tight text-[#F0E8D8]" style={{
          fontSize: 'clamp(42px, 12vw, 64px)',
          marginBottom: '0.16em'
        }} data-id="element-362" data-mp="1s0g64ts74ldc">
            Tu cuerpo
            <br data-id="element-363" data-mp="bo133f350qr4" />
            no falló.
          </motion.h2>

          {/* Concepto central */}
          <motion.p variants={phraseVariants} className="font-display italic leading-[1.08] text-[#C8A870]" style={{
          fontSize: 'clamp(26px, 6vw, 48px)',
          marginBottom: '0.25em'
        }} data-id="element-364" data-mp="6pgeye15txj1">
            Cambió la señal.
          </motion.p>

          {/* Señal H.E.R. — firma visual */}
          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true,
          amount: 0.6
        }} transition={{
          duration: 0.35,
          delay: 0.45
        }} className="mx-auto mb-5 flex w-full justify-center md:mb-7" aria-hidden="true" data-id="element-365" data-mp="2kpxjy41kmrai">
            <svg viewBox="0 0 240 32" className="h-auto w-[72%] max-w-[240px] overflow-visible" fill="none" data-id="element-366" data-mp="34dd8hlzgcnw3">
              {/* Señal: irregular → estable */}
              <motion.path d="
                  M6 17
                  C14 7 23 25 32 16
                  C41 7 50 24 60 15
                  C70 7 79 22 89 15
                  C99 9 108 19 119 16
                  C130 14 140 17 151 16
                  L210 16
                " stroke="#C8A870" strokeWidth="1.35" strokeLinecap="round" strokeLinejoin="round" initial={{
              pathLength: 0,
              opacity: 0
            }} whileInView={{
              pathLength: 1,
              opacity: 0.9
            }} viewport={{
              once: true,
              amount: 0.6
            }} transition={{
              pathLength: {
                duration: 1.1,
                ease: [0.22, 1, 0.36, 1],
                delay: 0.5
              },
              opacity: {
                duration: 0.25,
                delay: 0.5
              }
            }} data-id="element-367" data-mp="3dci7etexej8s" />

              {/* Punto final */}
              <motion.circle cx="216" cy="16" r="2.8" fill="#C8A870" initial={{
              opacity: 0,
              scale: 0.5
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true,
              amount: 0.6
            }} transition={{
              delay: 1.45,
              duration: 0.25,
              ease: [0.22, 1, 0.36, 1]
            }} style={{
              transformOrigin: '216px 16px'
            }} data-id="element-368" data-mp="1nzmtyddzt11v" />
            </svg>
          </motion.div>

          {/* Frase 3 — intriga */}
          <motion.p variants={phraseVariants} className="font-display text-[#F0E8D8]/55" style={{
          fontSize: 'clamp(15px, 3.5vw, 24px)'
        }} data-id="element-369" data-mp="2qe3vhn9twtq0">
            Y nadie te enseñó a leerla.
          </motion.p>
        </motion.div>

        {/* Puente explicativo */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7,
        delay: 0.35
      }} className="mx-auto mt-7 max-w-lg text-center md:mt-12" data-id="element-370" data-mp="39k1mrlprtoc6">
          <p className="font-body text-[#F0E8D8]/65" style={{
          fontSize: 'clamp(14px, 2.5vw, 18px)',
          lineHeight: 1.65
        }} data-id="element-371" data-mp="2qpcxe7ei5mi4">
            Seguís haciendo muchas de las cosas que antes te funcionaban.
            Pero la señal cambió, y tu cuerpo empezó a responder distinto.
            Lo que antes alcanzaba, ahora ya no siempre encaja.
          </p>
        </motion.div>

        {/* Separador */}
        <motion.div initial={{
        opacity: 0,
        scaleX: 0.5
      }} whileInView={{
        opacity: 1,
        scaleX: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.5
      }} className="divider-fine mx-auto mt-8 w-16 md:mt-12" data-id="element-372" data-mp="d33x6ogljkbx" />
      </div>
    </section>;
}